import BasicButton from "components/General/BasicButton";
import { useNavigate } from "react-router-dom"
import { useGameContext } from "GameContext";
import { useWebSocket } from "WebSocketContext";
import Timer from "components/Timer";
import WindowComponent from 'react-flexi-window'

import Chat from "components/Chat/Chat";
import ChatRoom from "components/Chat/ChatRoom";

import React, {
  useState,
  useEffect,
} from "react";

interface ChatHeaders {
  id: string;
  disabled: boolean;
}

const VoteView: React.FC = () => {
  const { socket } = useWebSocket();

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      const message = "Are you sure you want to leave? Data may be lost";
      if (!window.confirm(message)) {
        // User pressed "Cancel" → block unload
        event.preventDefault();
        event.returnValue = "";
      }
      // If user pressed "OK", allow unload
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  const { 
    gameId,
    hostId,
    timeLeft,
    players,
    isAdmin,
  } = useGameContext();
  const [chatWindows, setChatWindows] = useState<ChatHeaders[]>([]);

  const addChatWindow = (inputId : string) => {
    setChatWindows(prev => {
      const existing = prev.find(window => window.id === inputId);
  
      if (existing) {
        // If it exists and is disabled, enable it
        if (existing.disabled) {
          return prev.map(window =>
            window.id === inputId ? { ...window, disabled: false } : window
          );
        }
        // If it exists and is already enabled, do nothing
        return prev;
      }
  
      // If it doesn't exist, add new
      const newWindow: ChatHeaders = {
        id: inputId,
        disabled: false,
      };
      return [...prev, newWindow];
    });
  };

  const disableChatWindow = (inputId : string) => {
    setChatWindows(prev =>
      prev.map(window =>
        window.id === inputId ? { ...window, disabled: true } : window
      )
    );
  };

  const navigate = useNavigate()


  function onMessage(event: MessageEvent<string>) {
    if (event.data) {
      const message = JSON.parse(event.data);
      if (message.kind === "GoBackToGame") {
        navigate("/play/main");
      }

      if (message.kind === "VoteCounts") {
        setPlayersVoted(message.voteCount);
      }
    }
  }

  useEffect(() => {
    if (!socket) return;
    socket.addEventListener("message", onMessage);

    return () => {
      if (socket) {
        socket.removeEventListener("message", onMessage);
      }
    }
  }, [socket]);

  useEffect(() => {
    if (!gameId) {
      navigate("/play");
    }
  }, [gameId])

  const wRegimes = [1,2,3,4,5]

  const [selectedWm, setSelectedWm] = useState<number | null>(null);
  const [submittedVote, setSubmittedVote] = useState(false);

  const playerCount = players.length;
  const [playersVoted, setPlayersVoted] = useState(0);

  function submitWMVote() {
    const mes = JSON.stringify({
      kind: "WMVote",
      toIds: [hostId],
      wmRegime: selectedWm,
    });

    socket?.send( mes );

    setSubmittedVote(true);
  }

  return (
    <div className="flex h-[100vh] w-[100vw] overflow-hidden">
      <div className="flex flex-row h-fit border-b-2 border-r-2 border-black p-2 gap-4">
        <h1 className="text-2xl">Townhall</h1>

        { isAdmin && (
          <button onClick={() => {
            navigate("/play/main/host")
          }}>HOST CONTROLS</button>
        )}
      </div>

      <WindowComponent
        w={400}
        h={400}
  
        minW={100}
        minH={100}
  
        x={5000}
        y={5000}
        // windowColor={"black"} //blue-500/20"}
        boundary={true}
      >
        <Chat onRoomClick={addChatWindow} noback={true}/>
      </WindowComponent>

      {chatWindows.map((chat: ChatHeaders, index: number) => (
        <div style={{
          opacity: chat.disabled ? 0 : 1,         // lower opacity if disabled
          pointerEvents: chat.disabled ? "none" : "auto", // ignore mouse events if disabled
          position: 'absolute',
        }}>
          <WindowComponent 
            key={chat.id}           
            w={300}
            h={300}

            minW={100}
            minH={100}

            x={500 + (index * 5)}
            y={500 + (index * 5)}
            // windowColor={"black"} //blue-500/20"}
            boundary={true}
          >
            <ChatRoom
              roomId={chat.id}
              onBackClick={()=>disableChatWindow(chat.id)} 
            />
          </WindowComponent>
        </div>
      ))}

      <WindowComponent
        w={220}
        h={75}
  
        minW={75}
        minH={75}
  
        x={5000}
        y={0}
        // windowColor={"black"} //blue-500/20"}
        boundary={true}
      >
        <Timer time={timeLeft}/>
      </WindowComponent>

      <WindowComponent
        w={400}
        h={100}
  
        minW={100}
        minH={100}
  
        x={100}
        y={100}
        // windowColor={"black"} //blue-500/20"}
        boundary={true}
      >
        <div className="text-right flex flex-col h-full w-full border-2 border-black bg-[#BCB7A8] p-2">
          <label className="text-xl">Voting Status</label>
          <label className="text-lg">{`Waiting for ${playerCount - playersVoted} player to vote`}</label>
        </div>
      </WindowComponent>

      <WindowComponent
        w={500}
        h={600}
  
        minW={100}
        minH={100}
  
        x={100}
        y={100}
        // windowColor={"black"} //blue-500/20"}
        boundary={true}
      >
        <div className="flex flex-col h-full w-full border-2 border-black bg-[#BCB7A8] p-2">
          <h1 className="text-xl text-center">Submit your vote</h1>
          <div className="" />
          { submittedVote ? (
            <div className="flex flex-col grow items-center justify-center">
              <p className="text-lg text-center">Thank you for submitting your vote</p>
            </div>
          ) : (
            <div className="flex flex-col grow">
              <div className="flex flex-col items-center gap-2 pt-12">
                { wRegimes.map((wr, i) => (
                  <button className="flex flex-row items-center gap-2" key={i} onClick={() => {
                    if (selectedWm === wr) {
                      setSelectedWm(null)
                    } else {
                      setSelectedWm(wr)
                    }
                  }}>
                    <div className="h-6 w-6 border border-black rounded-full">
                      {selectedWm === wr && (
                        <div className="relative h-full w-full flex items-center justify-center">
                          <div className="absolute w-full bg-black h-1 rotate-45"/>
                          <div className="absolute w-full bg-black h-1 -rotate-45"/>
                        </div>
                      )}
                    </div>
                    <p className="text-lg">{`Water Regime ${wr}`}</p>
                  </button>
                ))}
              </div>

              <div className="grow flex" />

              { selectedWm && (
                <div className="flex flex-col items-center gap-2">
                  <p className="text-lg text-center">Confirm Selection? You cannot change after submitting your vote.</p>
                  <BasicButton text={"Submit"} onClick={submitWMVote} />
                </div>
              )}
              <div className="h-8" />
            </div>
          )}
        </div>
      </WindowComponent>

    </div>
  )
};

export default VoteView;
